import React, { Suspense, useEffect } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import "bootstrap/dist/css/bootstrap.min.css";
import "./main.css";
import { goToLocationReplace, useLocation } from "./utils/router";
import RenderPage from "./cms/RenderPage";
import { SiteMap } from "./cms/SiteMap";
import LoginCallback from "./pages/LoginCallback";
import { InternFrame, useInternMode } from "./intern/mode";
import { ErrorBoundary } from "react-error-boundary";

// DIE NEUN ROUTENSEITEN WERDEN NACHGELADEN.
//
// Warum: wer die Startseite oeffnet, bekam bis hierher AUCH die Admin-Tabellen
// (react-table, das Admin-Proto), den Anmeldeassistenten, den Lageplan mit
// Drag & Drop und die Projektansichten — gemessen 3.121.673 Byte roh /
// 904.848 gzip in EINEM Stueck, obwohl ein anonymer Besucher davon nur
// Navigation, Footer und den CMS-Renderer braucht.
//
// Der eigene Router (utils/router.tsx) braucht dafuer KEINE Aenderung: er
// liefert nur einen Pfad-String, die Auswahl trifft weiter unten ein
// if/switch. Getauscht wird allein, WIE die Seite in dieses Modul kommt.
//
// Bedingung, damit das wirkt: keine eifrig geladene Datei darf eine dieser
// Seiten noch statisch importieren. Geprueft — die Querbezuege laufen
// entweder innerhalb dieser neun (Workspace -> ProjectDetailPage,
// LageplanPage -> MixAnsichtPage) oder sind reine Typen (AreaProps aus
// AdminPage).
// Die Ladefunktionen stehen EINZELN da, damit dieselbe Funktion zweimal
// benutzt werden kann: einmal von React.lazy beim Rendern und einmal von
// warmRoute() vor dem ersten Rendern (siehe unten).
const ladeProjects = () => import("./pages/projekte/ProjectsPage");
const ladePersonen = () => import("./pages/projekte/personen/PersonenPage");
const ladeMixAnsicht = () => import("./pages/projekte/mix-ansicht/MixAnsichtPage");
const ladeLageplan = () => import("./pages/projekte/lageplan/LageplanPage");
const ladeProjectDetail = () => import("./pages/projekte/ProjectDetailPage");
const ladeAnmeldung = () => import("./pages/anmeldung/AnmeldungPage");
const ladeMeinProjekt = () => import("./pages/mein-projekt/MeinProjektPage");
const ladeInternProject = () => import("./intern/InternProjectPage");
const ladeAdmin = () => import("./pages/admin/AdminPage");

const ProjectsPage = React.lazy(ladeProjects);
const PersonenPage = React.lazy(ladePersonen);
const MixAnsichtPage = React.lazy(ladeMixAnsicht);
const LageplanPage = React.lazy(ladeLageplan);
const ProjectDetailPage = React.lazy(ladeProjectDetail);
const AnmeldungPage = React.lazy(ladeAnmeldung);
const MeinProjektPage = React.lazy(ladeMeinProjekt);
const InternProjectPage = React.lazy(ladeInternProject);
const AdminPage = React.lazy(ladeAdmin);

/**
 * Holt das Stueck, das die ERSTE Adresse braucht, BEVOR React zum ersten Mal
 * rendert (aufgerufen in src/index.tsx).
 *
 * WARUM: beim Aufruf einer Adresse von aussen - Lesezeichen, Rueckleitung vom
 * IdP, neu geladene Seite - waere die Reihenfolge sonst: Huelle sofort,
 * Inhalt eine Anfrage spaeter. Man saehe Navigation und Footer um einen
 * leeren Bereich mit Platzhalter, obwohl gar nicht navigiert wurde. Beim
 * Klicken IM Portal ist ein Platzhalter richtig; beim ersten Aufbau der Seite
 * ist er nur ein Rueckschritt gegenueber vorher, als alles in einem Stueck kam.
 *
 * Das Stueck wird parallel zum Rest geholt und ist klein (19-50 kB gegen die
 * ~900 kB des Einstiegs), kostet also praktisch keine Zeit.
 *
 * Fehlschlaege werden BEWUSST verschluckt: das Vorwaermen darf den Start nie
 * verhindern.
 *
 * ABER SIE SIND NICHT FOLGENLOS - hier stand einmal, React.lazy versuche es
 * beim Rendern noch einmal. Das stimmt nicht: ein zweites import() derselben
 * Adresse gibt die gemerkte Ablehnung zurueck, ohne ins Netz zu gehen
 * (nachgemessen in cms/RenderPage.tsx). Ein Netzhaenger in diesen ersten
 * Millisekunden verurteilt die Route also bis zum naechsten vollstaendigen
 * Neuladen - dann greift RouteFailed und bietet genau das an.
 */
export function warmRoute(pfad: string): Promise<unknown> {
  const feste: Record<string, () => Promise<unknown>> = {
    "/anmeldung": ladeAnmeldung,
    "/mein-projekt": ladeMeinProjekt,
    "/projekte": ladeProjects,
    "/projekte/personen": ladePersonen,
    "/projekte/mix-ansicht": ladeMixAnsicht,
    "/projekte/lageplan": ladeLageplan,
  };
  let lade = feste[pfad];
  if (!lade && (pfad === "/admin" || pfad.startsWith("/admin/"))) lade = ladeAdmin;
  // Jede andere Adresse unter /projekte/ ist eine Projektdetailseite. Welche
  // der beiden Ansichten es wird - oeffentlich oder intern -, entscheidet
  // sich erst im Rendern; deshalb hier beide, sie sind zusammen klein.
  if (!lade && /^\/projekte\/[^/]+/.test(pfad))
    return Promise.all([ladeProjectDetail(), ladeInternProject()]).catch(() => undefined);
  return lade ? lade().catch(() => undefined) : Promise.resolve();
}

/**
 * Was zu sehen ist, wenn ein Seitenstueck NICHT geladen werden konnte.
 *
 * Er steht in der Huelle, Navigation und Footer bleiben also stehen - der
 * Mensch kann weiterklicken, statt auf eine weisse Flaeche zu sehen. Der
 * zweite Weg ist ein Neuladen: nach einer neuen Ausgabe der Website zeigt
 * das alte HTML auf Dateinamen, die es nicht mehr gibt, und genau dann ist
 * ein Neuladen die richtige und einzige Abhilfe.
 */
function RouteFailed() {
  return (
    <div className="container py-5" data-testid="route-failed">
      <h1 className="h4">Dieser Teil der Seite liess sich nicht laden.</h1>
      <p className="text-body-secondary">
        Das passiert bei einer unterbrochenen Verbindung — oder wenn die
        Website gerade neu veröffentlicht wurde und dieser Teil unter einem
        neuen Namen liegt.
      </p>
      {/* KEIN "NOCHMAL VERSUCHEN". Hier stand einer, und er war eine
          Attrappe: React.lazy ruft die Ladefunktion nach einem Fehlschlag
          nicht erneut auf, und selbst ein frisches lazy geht nicht mehr ins
          Netz - die Modulkarte des Dokuments merkt sich die gescheiterte
          Adresse. Beides ist in cms/RenderPage.tsx mit Netzmitschnitt
          nachgemessen, und diese Datei hat es trotzdem angeboten.
          
          Ein Knopf, der nichts tut, ist schlimmer als kein Knopf - er laesst
          den Menschen dreimal klicken, bevor er das Richtige tut. Das
          Richtige ist ein frisches Dokument: neue Modulkarte, und nach einer
          Neuausgabe der Website ausserdem das Einzige, was die neuen
          Dateinamen kennt. Wer stattdessen weiterlesen will, klickt oben in
          der Navigation weiter - die steht ja noch. */}
      <button
        type="button"
        className="btn btn-primary"
        data-testid="route-reload"
        onClick={() => window.location.reload()}
      >
        Seite neu laden
      </button>
    </div>
  );
}

// Der Platzhalter fuer die Zeit zwischen Klick und fertigem Stueck.
//
// Er steht INNERHALB der Huelle (siehe Shell): Navigation und Footer bleiben
// waehrend des Nachladens stehen, es wechselt nur der Inhaltsbereich. Ein
// leerer weisser Schirm waere schlechter als die eingesparten Bytes.
//
// Sichtbar wird er erst nach 150 ms (Verzoegerung der Einblendung in
// main.css, .route-pending). Ein Stueck aus dem Cache oder vom lokalen Server
// ist vorher da — dann blitzt NICHTS auf, und der Nutzer sieht denselben
// Uebergang wie vor der Umstellung.
function RoutePending() {
  return (
    <div className="route-pending" role="status" aria-live="polite">
      <span className="spinner-border" aria-hidden="true" />
      <span className="visually-hidden">Seite wird geladen</span>
    </div>
  );
}

// Die gemeinsame Seitenhuelle: Navigation, Inhalt, Footer.
//
// Sie existiert, damit KURZE Seiten den Schirm trotzdem fuellen. Ohne sie
// beginnt der Footer direkt unter dem letzten Absatz — auf /anmeldung, /admin
// und /mein-projekt mitten im Bild. Die Regeln stehen in main.css
// (.page-shell / .page-main); hier wird nur sichergestellt, dass JEDE Route
// durch dieselbe Huelle geht und keine sie vergisst.
function Shell(props: { children: React.ReactNode }) {
  // Der Ort ist der Ruecksetzschluessel: wer nach einem Fehlschlag
  // weiterklickt, soll die naechste Seite sehen und nicht die Fehlermeldung
  // der vorigen. Ohne ihn bliebe die Boundary bis zum Neuladen stehen.
  const ort = useLocation();
  // Der Footer steht BEWUSST ausserhalb der Huelle. Er ist auf dieser Seite
  // ueber 800px hoch (Sponsorenband) — laege er drin, waere die Huelle schon
  // ohne Inhalt hoeher als der Schirm, und fuer den Inhalt bliebe nichts mehr
  // zu verteilen. Ausserhalb gilt: Navigation + Inhalt fuellen mindestens
  // einen Schirm, der Footer beginnt daher fruehestens an der Falz.
  return (
    <>
      <div className="page-shell">
        <Navbar />
        <div className="page-main">
          {/* EIN Auffangpunkt fuer alle nachgeladenen Seiten, bewusst HIER
              und nicht weiter aussen: liegt er ueber der Huelle, verschwaenden
              waehrend des Nachladens auch Navigation und Footer.
              
              DIE ERRORBOUNDARY IST KEIN BEIWERK. Suspense faengt das WARTEN,
              nicht das SCHEITERN: schlaegt das Nachladen eines Stuecks fehl -
              Netz weg, Datei nach einer Neuausgabe nicht mehr da -, laeuft
              die Ablehnung bis zur Wurzel durch und React haengt den ganzen
              Baum aus. Gemessen: weisse Seite, keine Navigation, kein Footer,
              keine Erholung. Also genau das, was der Kommentar am Platzhalter
              auszuschliessen verspricht ("Ein leerer weisser Schirm waere
              schlechter als die eingesparten Bytes"). Vor der Aufteilung gab
              es diesen Fall nicht: was nicht nachgeladen wird, kann auch
              nicht fehlschlagen. */}
          <ErrorBoundary
            FallbackComponent={RouteFailed}
            resetKeys={[ort]}
          >
            <Suspense fallback={<RoutePending />}>{props.children}</Suspense>
          </ErrorBoundary>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default function App() {
  const location = useLocation();
  const intern = useInternMode();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, [location]);

  // check if old url is used: /projects* -> /projekte*
  if (location.startsWith("/projects")) {
    setTimeout(
      () => goToLocationReplace(location.replace("/projects", "/projekte")),
      1,
    );
    return <div>Redirecting...</div>;
  }

  // Die Rueckleitung des IdP. Bewusst OHNE Navbar und Footer: die Seite lebt
  // nur Sekundenbruchteile und leitet dann dorthin zurueck, wo die Anmeldung
  // begonnen hat (utils/auth.ts, processResponse).
  if (location === "/login/callback") {
    return <LoginCallback />;
  }

  // Der Admin-Bereich (docs/02-konzept.md §8.2). Er hiess bis Ende August 2026
  // „Cockpit"; der alte Name lebt nur noch im Dateinamen der angewandten
  // Migration db/migrations/0021_cockpit.sql weiter, wo er ein Schluessel ist
  // und keine Beschriftung.
  //
  // Er haengt NICHT am Admin-Modus: der Modus macht einzelne Objekte dort
  // bearbeitbar, wo man sie sieht; der Admin-Bereich ist das, was ueber ein
  // einzelnes Objekt hinausgeht. Wer die Rechte hat, kommt auch ohne
  // eingeschalteten Schalter hin.
  if (location === "/admin" || location.startsWith("/admin/")) {
    // BEWUSST OHNE InternFrame. Der Admin-Modus ist die Bearbeitung DORT, WO
    // MAN SIE SIEHT — auf den oeffentlichen Seiten (Entscheid E5,
    // docs/02-konzept.md §8). Im Admin-Bereich ist ohnehin alles editierbar; ein
    // Rahmen mit dem Band "Aenderungen wirken sofort" sagt dort nichts, was
    // nicht schon gilt, und der Schalter suggeriert einen Unterschied, den es
    // nicht gibt. Der Schalter selbst wird in der Navigation auf dieser Route
    // ausgeblendet (components/Navbar.tsx).
    return (
      <Shell>
        <AdminPage path={location} />
      </Shell>
    );
  }

  const projectDetail = ProjectRoutes(location, intern.active);
  if (projectDetail) {
    return (
      <InternFrame active={intern.active}>
        <Shell>{projectDetail}</Shell>
      </InternFrame>
    );
  }

  // Static routes
  const Route = Routes[location];
  if (Route) {
    return (
      <InternFrame active={intern.active}>
        <Shell>
          <Route />
        </Shell>
      </InternFrame>
    );
  }

  return (
    <InternFrame active={intern.active}>
      <Shell>
        <RenderPage path={location} />
      </Shell>
    </InternFrame>
  );
}

const Routes: Record<string, React.ComponentType<any>> = {
  "/sitemap": SiteMap,

  // Anmeldung und Einladung (docs/02-konzept.md §5). Weg A und Weg B teilen
  // sich dieselbe Route; der Unterschied steht in der Abfrage
  // (?token=… beziehungsweise ?einladung=…).
  "/anmeldung": AnmeldungPage,
  "/mein-projekt": MeinProjektPage,

  // Projekte
  //
  // NUR die Uebersicht. /projekte/lageplan, /projekte/personen und
  // /projekte/mix-ansicht standen hier ebenfalls und waren UNERREICHBAR:
  // ProjectRoutes laeuft davor und faengt jede Adresse unter /projekte/ ab.
  //
  // Schlimmer als tot: die Eintraege hier haetten die Seiten OHNE das
  // Kennzeichen intern gerendert - LageplanPage und PersonenPage brauchen es
  // fuer ihre Adminansicht. Waere die Reihenfolge je vertauscht worden,
  // haette der Admin-Modus in genau diesen beiden Seiten still aufgehoert zu
  // wirken.
  "/projekte": ProjectsPage,
};

// Check if the location is a project detail page
//
// Der Admin-Modus tauscht hier die Seite: statt ProjectDetailPage kommt
// InternProjectPage. Die laedt ueber den AdminService und rendert danach
// DENSELBEN ProjectWorkspace, den auch /mein-projekt rendert — dieselben
// Abschnitte, dieselben Felder, dieselbe Autosave-Mechanik, nur mit
// Schreibvorgaengen an den AdminService (docs/02-konzept.md §8.1).
//
// FRUEHER STAND HIER: dieselbe ProjectDetailPage mit einem edit-Flag. Das
// Flag ist ersatzlos entfernt — es machte Titel und Beschreibung
// bearbeitbar und sonst nichts (intern/InternProjectPage.tsx erklaert, warum
// das zu wenig war). „Ein Codepfad" gilt weiterhin, er laeuft nur ueber die
// Bearbeitungsoberflaeche statt ueber die Leseoberflaeche.
//
// Lageplan und Personen gehen einen anderen Weg und bekommen deshalb nur das
// Kennzeichen intern durchgereicht: PersonenPage ersetzt einzelne Felder der
// oeffentlichen Karte (intern/Editable.tsx), LageplanPage hat mit
// InternLagePlan eine eigene Ansicht mit Drag & Drop.
function ProjectRoutes(location: string, intern: boolean) {
  const projectDetailMatch = location.match(/\/projekte\/([^\/]+)/);
  if (projectDetailMatch) {
    const projectId = projectDetailMatch[1];
    switch (projectId) {
      case "lageplan":
        return <LageplanPage intern={intern} />;
      case "personen":
        return <PersonenPage intern={intern} />;
      case "mix-ansicht":
        return <MixAnsichtPage />;
      default:
        return intern ? (
          <InternProjectPage projectID={projectId} />
        ) : (
          <ProjectDetailPage projectID={projectId} />
        );
    }
  }
}
